import React, { useState } from 'react';
import { X, Link2, Upload, Loader2, Film } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { captureFirstFrame } from '@/utils/videoThumbnail';
import API_URL from '@/config/api';
import { formatApiError } from '@/utils/apiError';

const MAX_UPLOAD_MB = 500;

/**
 * Add / Edit video modal. Dual-mode input:
 *   • "YouTube link" tab — paste any external URL (YouTube, Vimeo, etc.)
 *   • "Upload file"  tab — pick a file, auto-grab a first-frame thumbnail
 *
 * The upload flow POSTs to /videos/upload (binary) + /videos/upload-thumbnail
 * (JPEG) first, then the parent's `onSave` handler runs against the plain
 * /videos endpoint with the returned `storage_path` fields. Keeping the
 * upload step here means the parent's save-flow only ever sees ready-to-go
 * metadata, no half-finished files to reconcile if the modal closes early.
 */
export default function VideoFormModal({
  open,
  editing,
  videoForm,
  setVideoForm,
  allSections,
  toggleSection,
  getAvailableCategories,
  colors,
  onClose,
  onSave,
}) {
  // "url" = paste a link (YouTube etc.), "upload" = pick a file
  // Edit mode: default to whichever source the video already uses.
  const initialMode = editing?.storage_path ? 'upload' : 'url';
  const [mode, setMode] = useState(initialMode);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [fileMeta, setFileMeta] = useState(null); // {name, size, storage_path, thumb_path}

  if (!open) return null;

  const resetUploadState = () => {
    setUploading(false);
    setProgress(0);
    setFileMeta(null);
  };

  const handleFilePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Guard-rail before we push half a gig up the wire
    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > MAX_UPLOAD_MB) {
      toast.error(`Video is ${Math.round(sizeMB)} MB — max is ${MAX_UPLOAD_MB} MB. Trim or compress it first.`);
      e.target.value = '';
      return;
    }

    setUploading(true);
    setProgress(5);

    try {
      // 1) Capture the first frame in the browser — small JPEG, uploads fast
      // and never round-trips ffmpeg on the server.
      setProgress(10);
      let thumbBlob = null;
      try {
        thumbBlob = await captureFirstFrame(file, { maxWidth: 1280, quality: 0.72 });
      } catch (err) {
        // Non-fatal — the video itself still works with a placeholder poster.
        console.warn('Thumbnail capture failed, continuing without it:', err);
      }
      setProgress(25);

      // 2) Kick off the video upload. The Kubernetes ingress in front of
      // FastAPI rejects any single POST over ~100 MB with a 413 —
      // exactly what installers were hitting at 25 %. We now split the
      // file into 5 MB chunks, POST them sequentially to `/videos/upload-chunk`,
      // and finalize at the end. Each chunk stays well under the proxy limit.
      const CHUNK_SIZE = 5 * 1024 * 1024; // 5 MB — safely under 100 MB proxy cap
      const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
      const uploadId = (crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`).replace(/[^a-zA-Z0-9_-]/g, '');
      let videoRes = null;
      for (let i = 0; i < totalChunks; i++) {
        const from = i * CHUNK_SIZE;
        const to = Math.min(file.size, from + CHUNK_SIZE);
        const blob = file.slice(from, to);
        const fd = new FormData();
        fd.append('upload_id', uploadId);
        fd.append('chunk_index', String(i));
        fd.append('total_chunks', String(totalChunks));
        fd.append('filename', file.name);
        fd.append('chunk', blob, `${file.name}.part${i}`);
        const r = await fetch(`${API_URL}/api/videos/upload-chunk`, {
          method: 'POST', credentials: 'include', body: fd,
        });
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          throw new Error(formatApiError(err) || `Chunk ${i + 1}/${totalChunks} failed (${r.status})`);
        }
        // Scale progress from 25 → 85 across all chunks so the bar
        // moves smoothly for the whole upload.
        setProgress(25 + Math.round((60 * (i + 1)) / totalChunks));
      }
      // Assemble on the backend.
      const finalizeFd = new FormData();
      finalizeFd.append('upload_id', uploadId);
      finalizeFd.append('filename', file.name);
      const videoResp = await fetch(`${API_URL}/api/videos/upload-finalize`, {
        method: 'POST', credentials: 'include', body: finalizeFd,
      });
      setProgress(85);
      if (!videoResp.ok) {
        const err = await videoResp.json().catch(() => ({}));
        throw new Error(formatApiError(err) || `Video finalize failed (${videoResp.status})`);
      }
      videoRes = await videoResp.json();

      let thumbRes = null;
      if (thumbBlob) {
        const thumbFd = new FormData();
        thumbFd.append('file', thumbBlob, 'thumb.jpg');
        const thumbResp = await fetch(`${API_URL}/api/videos/upload-thumbnail`, {
          method: 'POST',
          credentials: 'include',
          body: thumbFd,
        });
        if (thumbResp.ok) {
          thumbRes = await thumbResp.json();
        }
      }
      setProgress(100);

      setFileMeta({
        name: file.name,
        size: file.size,
        storage_path: videoRes.storage_path,
        content_type: videoRes.content_type,
        size_bytes: videoRes.size_bytes,
        thumbnail_storage_path: thumbRes?.thumbnail_storage_path || null,
      });

      // Fold the storage refs into the parent's form so onSave uses them.
      setVideoForm({
        ...videoForm,
        storage_path: videoRes.storage_path,
        content_type: videoRes.content_type,
        size_bytes: videoRes.size_bytes,
        thumbnail_storage_path: thumbRes?.thumbnail_storage_path || null,
        // Clear video_url so the row is clearly upload-sourced.
        video_url: '',
      });

      toast.success('Upload ready — fill in the details and hit Save.');
    } catch (err) {
      toast.error(err.message);
      resetUploadState();
    } finally {
      setUploading(false);
    }
  };

  // Whichever mode we're in, the SAVE button needs a valid title + category
  // + AT LEAST one source (URL or a completed upload).
  const canSave =
    !!videoForm.title.trim() &&
    !!videoForm.category_id &&
    ((mode === 'url' && !!videoForm.video_url?.trim()) ||
      (mode === 'upload' && !!videoForm.storage_path));

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-800">
            {editing ? 'Edit Video' : 'Add Video'}
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="video-form-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <Label>Video Title *</Label>
            <Input
              value={videoForm.title}
              onChange={(e) => setVideoForm({ ...videoForm, title: e.target.value })}
              placeholder="e.g. Surface Preparation Basics"
              className="mt-1"
              data-testid="video-form-title"
            />
          </div>
          <div>
            <Label>Description</Label>
            <textarea
              value={videoForm.description}
              onChange={(e) => setVideoForm({ ...videoForm, description: e.target.value })}
              className="mt-1 w-full border-2 rounded-lg p-3 min-h-[80px]"
              placeholder="Brief description of the video..."
              data-testid="video-form-description"
            />
          </div>
          <div>
            <Label>Visible in Sections *</Label>
            <p className="text-xs text-gray-500 mb-2">Select which sections this video should appear in</p>
            <div className="flex flex-wrap gap-2">
              {allSections.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  data-testid={`video-form-section-${s.id}`}
                  onClick={() => toggleSection(s.id)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border-2 ${
                    videoForm.sections?.includes(s.id)
                      ? 'text-white border-transparent'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                  }`}
                  style={videoForm.sections?.includes(s.id) ? { backgroundColor: s.color } : {}}
                >
                  {s.name}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label>Category *</Label>
            <p className="text-xs text-gray-500 mb-1">Categories from selected sections</p>
            <select
              value={videoForm.category_id}
              onChange={(e) => setVideoForm({ ...videoForm, category_id: e.target.value })}
              className="mt-1 w-full border-2 rounded-lg p-2.5"
              data-testid="video-form-category"
            >
              <option value="">Select a category</option>
              {getAvailableCategories().map((cat) => (
                <option key={cat.id} value={cat.id}>
                  [{cat.sectionName}] {cat.name}
                </option>
              ))}
            </select>
          </div>

          {/* ─── Source selector: paste URL vs upload file ─── */}
          <div className="border-t pt-4">
            <Label className="mb-2 block">Video source *</Label>
            <div className="flex gap-2 mb-3">
              <button
                type="button"
                onClick={() => setMode('url')}
                className={`flex-1 px-3 py-2 rounded-lg border-2 text-sm font-medium inline-flex items-center justify-center gap-2 ${
                  mode === 'url' ? 'border-[#7A0B7A] bg-[#7A0B7A]/5 text-[#7A0B7A]' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
                data-testid="video-mode-url"
              >
                <Link2 className="w-4 h-4" />
                Paste YouTube link
              </button>
              <button
                type="button"
                onClick={() => setMode('upload')}
                className={`flex-1 px-3 py-2 rounded-lg border-2 text-sm font-medium inline-flex items-center justify-center gap-2 ${
                  mode === 'upload' ? 'border-[#7A0B7A] bg-[#7A0B7A]/5 text-[#7A0B7A]' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
                data-testid="video-mode-upload"
              >
                <Upload className="w-4 h-4" />
                Upload video file
              </button>
            </div>

            {mode === 'url' && (
              <div>
                <Input
                  value={videoForm.video_url || ''}
                  onChange={(e) => setVideoForm({ ...videoForm, video_url: e.target.value, storage_path: null })}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="mt-1"
                  data-testid="video-form-url"
                />
                <p className="text-xs text-gray-500 mt-1">Works with YouTube, Vimeo, and other public video URLs.</p>
              </div>
            )}

            {mode === 'upload' && (
              <div>
                {!fileMeta && !uploading && (
                  <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-6 cursor-pointer hover:border-[#7A0B7A] transition-colors" data-testid="video-file-drop">
                    <Film className="w-8 h-8 text-gray-400 mb-2" />
                    <span className="text-sm font-medium text-gray-700">Choose a video file</span>
                    <span className="text-xs text-gray-500 mt-1">mp4, mov, webm — up to {MAX_UPLOAD_MB} MB</span>
                    <input
                      type="file"
                      accept="video/mp4,video/quicktime,video/webm,video/x-m4v,.mp4,.mov,.webm,.m4v"
                      className="hidden"
                      onChange={handleFilePick}
                      data-testid="video-file-input"
                    />
                  </label>
                )}
                {uploading && (
                  <div className="border-2 border-[#7A0B7A]/30 rounded-lg p-4 bg-[#7A0B7A]/5">
                    <div className="flex items-center gap-2 mb-2">
                      <Loader2 className="w-4 h-4 animate-spin text-[#7A0B7A]" />
                      <span className="text-sm font-medium text-[#7A0B7A]">Uploading video… {progress}%</span>
                    </div>
                    <div className="h-2 bg-white rounded-full overflow-hidden">
                      <div className="h-full bg-[#7A0B7A] transition-all" style={{ width: `${progress}%` }} />
                    </div>
                    <p className="text-xs text-gray-600 mt-2">Keep this tab open until it finishes.</p>
                  </div>
                )}
                {fileMeta && !uploading && (
                  <div className="border-2 border-emerald-200 bg-emerald-50 rounded-lg p-3 flex items-start gap-3" data-testid="video-uploaded-summary">
                    <Film className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-emerald-900 truncate">{fileMeta.name}</p>
                      <p className="text-xs text-emerald-700">
                        {(fileMeta.size / (1024 * 1024)).toFixed(1)} MB · Uploaded
                        {fileMeta.thumbnail_storage_path ? ' · Thumbnail captured' : ' · No thumbnail'}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        resetUploadState();
                        setVideoForm({ ...videoForm, storage_path: null, thumbnail_storage_path: null, content_type: null, size_bytes: null });
                      }}
                      className="text-xs text-emerald-700 hover:underline"
                    >
                      Replace
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div>
            <Label>Duration (optional)</Label>
            <Input
              value={videoForm.duration}
              onChange={(e) => setVideoForm({ ...videoForm, duration: e.target.value })}
              placeholder="e.g. 12:30"
              className="mt-1"
              data-testid="video-form-duration"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1" data-testid="video-form-cancel">
              Cancel
            </Button>
            <Button
              onClick={onSave}
              disabled={!canSave || uploading}
              style={{ backgroundColor: colors.primary }}
              className="flex-1 text-white"
              data-testid="video-form-save"
            >
              {editing ? 'Save' : 'Add Video'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
